import { Scene } from './AnimationCanvas';
import { alpha, clear, MONO, Palette, pick, random } from './helpers';

// Only the scenes the home page's cards use, so the page does not ship the lab's whole library.

const RED_HAT = '"Red Hat Display", sans-serif';

const approach = (value: number, target: number, delta: number, rate: number) =>
    value + (target - value) * Math.min(1, delta * rate);

const labelSize = (height: number) => Math.max(8, Math.min(12, height / 18));

const roundRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
) => {
    const radius = Math.max(0, Math.min(r, Math.min(Math.abs(w), Math.abs(h)) / 2));
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
};

const pointer = (ctx: CanvasRenderingContext2D, x: number, y: number, colour: string) => {
    ctx.fillStyle = colour;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + 13);
    ctx.lineTo(x + 3.5, y + 10);
    ctx.lineTo(x + 6, y + 15);
    ctx.lineTo(x + 8, y + 14);
    ctx.lineTo(x + 5.5, y + 9.5);
    ctx.lineTo(x + 10, y + 9.5);
    ctx.closePath();
    ctx.fill();
};

const label = (ctx: CanvasRenderingContext2D, height: number, scale = 1) => {
    const size = Math.max(8, Math.min(12, height / 18)) * scale;
    ctx.font = `${size}px ${MONO}`;
    ctx.textBaseline = 'middle';
    return size;
};

const ease = (value: number, target: number, delta: number, k = 6) =>
    value + (target - value) * Math.min(1, delta * k);

const gaussian = () => {
    let s = 0;
    for (let i = 0; i < 6; i++) s += Math.random();
    return (s - 3) * 1.41;
};

export const wireframe = (palette: Palette, mode: 'terrain' | 'cube' | 'sphere' = 'terrain'): Scene => {
    let offset = 0;

    return (frame) => {
        clear(frame, palette);
        const { ctx, width, height, delta, time } = frame;
        offset += delta * 0.55;
        ctx.lineWidth = 1;

        if (mode === 'terrain') {
            const rows = 14;
            const cols = 16;
            for (let r = 0; r < rows; r++) {
                const depth = r / rows;
                const y = height * (0.35 + depth * 0.6);
                ctx.strokeStyle = alpha(pick(palette, r), 0.15 + depth * 0.5);
                ctx.beginPath();
                for (let c = 0; c <= cols; c++) {
                    const t = c / cols;
                    const wave =
                        Math.sin(t * 6 + offset + r * 0.4) * 6 + Math.sin(t * 13 - offset * 1.4) * 3;
                    const x = t * width;
                    const py = y + wave * (0.4 + depth);
                    if (c === 0) ctx.moveTo(x, py);
                    else ctx.lineTo(x, py);
                }
                ctx.stroke();
            }
            return;
        }

        const cx = width / 2;
        const cy = height / 2;
        const scale = Math.min(width, height) * 0.2;
        const a = time * 0.7;
        const project = (x: number, y: number, z: number) => {
            const rx = x * Math.cos(a) - z * Math.sin(a);
            const rz = x * Math.sin(a) + z * Math.cos(a);
            const ry = y * Math.cos(a * 0.6) - rz * Math.sin(a * 0.6);
            const rz2 = y * Math.sin(a * 0.6) + rz * Math.cos(a * 0.6);
            const p = 2.6 / (2.6 + rz2);
            return [cx + rx * scale * p, cy + ry * scale * p] as const;
        };

        if (mode === 'cube') {
            const v = [
                [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
                [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1],
            ];
            const e = [[0,1],[1,2],[2,3],[3,0],[4,5],[5,6],[6,7],[7,4],[0,4],[1,5],[2,6],[3,7]];
            e.forEach(([i, j], k) => {
                const [x1, y1] = project(v[i][0], v[i][1], v[i][2]);
                const [x2, y2] = project(v[j][0], v[j][1], v[j][2]);
                ctx.strokeStyle = alpha(pick(palette, k), 0.8);
                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.stroke();
            });
            return;
        }

        const rings = 9;
        for (let r = 0; r < rings; r++) {
            const phi = (r / (rings - 1)) * Math.PI - Math.PI / 2;
            ctx.strokeStyle = alpha(pick(palette, r), 0.55);
            ctx.beginPath();
            for (let s = 0; s <= 36; s++) {
                const theta = (s / 36) * Math.PI * 2;
                const [px, py] = project(
                    Math.cos(phi) * Math.cos(theta),
                    Math.sin(phi),
                    Math.cos(phi) * Math.sin(theta)
                );
                if (s === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            }
            ctx.stroke();
        }

        const meridians = 10;
        for (let m = 0; m < meridians; m++) {
            const theta = (m / meridians) * Math.PI * 2;
            ctx.strokeStyle = alpha(pick(palette, m), 0.32);
            ctx.beginPath();
            for (let s = 0; s <= 24; s++) {
                const phi = (s / 24) * Math.PI - Math.PI / 2;
                const [px, py] = project(
                    Math.cos(phi) * Math.cos(theta),
                    Math.sin(phi),
                    Math.cos(phi) * Math.sin(theta)
                );
                if (s === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            }
            ctx.stroke();
        }
    };
};

export const presenceCursors = (palette: Palette): Scene => {
    type Word = { w: number; fade: number };
    type Line = { words: Word[]; fade: number; removing: boolean };

    const makeWords = (target: number): Word[] => {
        const words: Word[] = [];
        let total = 0;
        while (total < target) {
            const w = random(0.05, 0.14);
            words.push({ w: Math.min(w, target - total), fade: 1 });
            total += w;
        }
        return words;
    };

    const makeLine = (i: number): Line => ({
        words: makeWords(i % 4 === 3 ? random(0.45, 0.65) : random(0.8, 1)),
        fade: 1,
        removing: false,
    });

    const lines: Line[] = Array.from({ length: 9 }, (_, i) => makeLine(i));
    const MIN_LINES = 6;
    const MAX_LINES = 12;
    let editNext = random(1.6, 3);

    type Cursor = {
        name: string;
        tint: number;
        line: number;
        u: number;
        x: number;
        y: number;
        next: number;
        selLine: number;
        selStart: number;
        selEnd: number;
        selecting: boolean;
        userDriven: boolean;
    };
    const cursors: Cursor[] = ['ana', 'kai', 'mo', 'zoe'].map((name, i) => ({
        name,
        tint: i,
        line: (i * 2 + 1) % lines.length,
        u: random(0.1, 0.8),
        x: -1,
        y: -1,
        next: random(0.4, 1.6),
        selLine: 0,
        selStart: 0,
        selEnd: 0,
        selecting: false,
        userDriven: false,
    }));
    let customer: Cursor | null = null;

    return (frame) => {
        clear(frame, palette);
        const { ctx, width, height, delta, time, mouseX, mouseY, hovering, clicks } = frame;
        const pad = 12;
        const size = labelSize(height);
        const top = pad + size * 0.9;
        const textW = width - pad * 2 - 30;

        /* Keep the document alive: lines fade in and out instead of sitting static forever. */
        if (time > editNext) {
            editNext = time + random(1.8, 3.4);
            const removable = lines.length > MIN_LINES;
            const insertable = lines.length < MAX_LINES;
            if (insertable && (!removable || Math.random() < 0.55)) {
                lines.splice(Math.floor(random(0, lines.length + 1)), 0, { ...makeLine(lines.length), fade: 0 });
            } else if (removable) {
                const candidates = lines.map((l, i) => i).filter((i) => !lines[i].removing);
                if (candidates.length) lines[candidates[Math.floor(random(0, candidates.length))]].removing = true;
            }
        }
        for (let i = lines.length - 1; i >= 0; i--) {
            const line = lines[i];
            if (line.removing) {
                line.fade = Math.max(0, line.fade - delta * 2.2);
                if (line.fade === 0) {
                    lines.splice(i, 1);
                    cursors.forEach((c) => {
                        if (c.line >= i && c.line > 0) c.line--;
                        if (c.selLine >= i && c.selLine > 0) c.selLine--;
                    });
                }
            } else if (line.fade < 1) {
                line.fade = Math.min(1, line.fade + delta * 2.2);
            }
        }

        const lineH = (height - top - pad) / Math.max(1, lines.length);
        const barH = Math.max(3, Math.min(6, lineH * 0.32));

        ctx.font = `${size}px ${RED_HAT}`;
        ctx.textBaseline = 'top';

        lines.forEach((line, i) => {
            const y = top + i * lineH + lineH / 2 - barH / 2;
            let x = pad;
            line.words.forEach((word) => {
                ctx.fillStyle = alpha(palette.foreground, (i % 4 === 0 ? 0.42 : 0.2) * line.fade);
                ctx.fillRect(x, y, word.w * textW - 4, barH);
                x += word.w * textW;
            });
        });

        const clampLine = (l: number) => Math.max(0, Math.min(lines.length - 1, l));

        cursors.forEach((cursor) => {
            cursor.line = clampLine(cursor.line);
            cursor.selLine = clampLine(cursor.selLine);
            if (cursor.userDriven || lines.length === 0) return;
            if (time > cursor.next) {
                cursor.next = time + random(1.2, 2.6);
                cursor.line = Math.floor(random(0, lines.length));
                const span = lines[cursor.line].words.reduce((a, b) => a + b.w, 0);
                cursor.u = random(0.02, Math.max(0.03, span - 0.05));
                if (Math.random() < 0.4) {
                    cursor.selecting = true;
                    cursor.selLine = cursor.line;
                    cursor.selStart = cursor.u;
                    cursor.selEnd = cursor.u;
                    cursor.u = Math.min(span, cursor.u + random(0.15, 0.4));
                } else {
                    cursor.selecting = false;
                }
            }
            const tx = pad + cursor.u * textW;
            const ty = top + cursor.line * lineH + lineH / 2;
            cursor.x = cursor.x < 0 ? tx : approach(cursor.x, tx, delta, 4.5);
            cursor.y = cursor.y < 0 ? ty : approach(cursor.y, ty, delta, 4.5);
            if (cursor.selecting && cursor.line === cursor.selLine) {
                cursor.selEnd = Math.max(cursor.selStart, (cursor.x - pad) / textW);
            }
        });

        /* The visitor's own cursor: created on first click, repositioned on every click after. */
        clicks.forEach((click) => {
            const line = clampLine(Math.floor((click.y - top) / lineH));
            const u = Math.max(0.02, Math.min(0.96, (click.x - pad) / textW));
            if (!customer) {
                customer = {
                    name: 'new customer',
                    tint: 4,
                    line,
                    u,
                    x: click.x,
                    y: click.y,
                    next: Infinity,
                    selLine: line,
                    selStart: u,
                    selEnd: u,
                    selecting: false,
                    userDriven: true,
                };
                cursors.push(customer);
            } else {
                customer.line = line;
                customer.u = u;
                if (Math.random() < 0.5) {
                    customer.selecting = true;
                    customer.selLine = line;
                    customer.selStart = u;
                    customer.selEnd = Math.min(0.98, u + random(0.1, 0.3));
                } else {
                    customer.selecting = false;
                }
            }
        });
        if (customer) {
            const tx = pad + customer.u * textW;
            const ty = top + customer.line * lineH + lineH / 2;
            customer.x = approach(customer.x, tx, delta, 6);
            customer.y = approach(customer.y, ty, delta, 6);
        }

        cursors.forEach((cursor) => {
            if (!cursor.selecting) return;
            const y = top + cursor.selLine * lineH + lineH / 2;
            ctx.fillStyle = alpha(pick(palette, cursor.tint), 0.28);
            ctx.fillRect(
                pad + cursor.selStart * textW,
                y - lineH * 0.36,
                (cursor.selEnd - cursor.selStart) * textW,
                lineH * 0.72
            );
        });

        ctx.textBaseline = 'middle';
        cursors.forEach((cursor) => {
            const colour = pick(palette, cursor.tint);
            ctx.fillStyle = colour;
            ctx.fillRect(cursor.x - 1, cursor.y - lineH * 0.38, 2, lineH * 0.76);
            const tagW = ctx.measureText(cursor.name).width + 10;
            const tagH = size + 6;
            const tagX = Math.min(width - pad - tagW, cursor.x - 1);
            const tagY = cursor.y - lineH * 0.38 - tagH - 2;
            roundRect(ctx, tagX, tagY, tagW, tagH, 3);
            ctx.fill();
            ctx.fillStyle = palette.background;
            ctx.fillText(cursor.name, tagX + 5, tagY + tagH / 2);
        });

        /* A faint ring follows the visitor's own pointer while it hovers the card. */
        if (hovering && mouseX !== null && mouseY !== null) {
            ctx.strokeStyle = alpha(pick(palette, 4), 0.55);
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.arc(mouseX, mouseY, 7, 0, Math.PI * 2);
            ctx.stroke();
        }
    };
};

export const mainframeJcl = (palette: Palette): Scene => {
    const STEP_IDX = [1, 4, 8, 11];
    const JOBS: string[][] = [
        [
            '//PAYRUN   JOB (ACCT),\'NIGHTLY PAY\',CLASS=A,MSGCLASS=X',
            '//STEP010  EXEC PGM=SORT',
            '//SORTIN   DD DSN=PROD.PAY.INPUT(0),DISP=SHR',
            '//SORTOUT  DD DSN=PROD.PAY.SORTED,DISP=(NEW,CATLG)',
            '//STEP020  EXEC PGM=PAYCALC,COND=(4,LT)',
            '//INPUT    DD DSN=PROD.PAY.SORTED,DISP=SHR',
            '//RATES    DD DSN=PROD.PAY.RATES,DISP=SHR',
            '//OUTPUT   DD DSN=PROD.PAY.RESULT(+1),DISP=(NEW,CATLG)',
            '//STEP030  EXEC PGM=IEBGENER,COND=(4,LT)',
            '//SYSUT1   DD DSN=PROD.PAY.RESULT(+1),DISP=SHR',
            '//SYSUT2   DD SYSOUT=*',
            '//STEP040  EXEC PGM=BACSOUT,COND=(0,NE)',
            '//BACSFILE DD DSN=PROD.BACS.SUBMIT,DISP=(NEW,CATLG)',
        ],
        [
            '//GLPOST   JOB (ACCT),\'GL DAILY POST\',CLASS=A,MSGCLASS=X',
            '//STEP010  EXEC PGM=SORT',
            '//SORTIN   DD DSN=PROD.GL.JOURNAL,DISP=SHR',
            '//SORTOUT  DD DSN=PROD.GL.SORTED,DISP=(NEW,CATLG)',
            '//STEP020  EXEC PGM=GLPOST01,COND=(4,LT)',
            '//INPUT    DD DSN=PROD.GL.SORTED,DISP=SHR',
            '//COA      DD DSN=PROD.GL.COA,DISP=SHR',
            '//OUTPUT   DD DSN=PROD.GL.POSTED(+1),DISP=(NEW,CATLG)',
            '//STEP030  EXEC PGM=IEBGENER,COND=(4,LT)',
            '//SYSUT1   DD DSN=PROD.GL.POSTED(+1),DISP=SHR',
            '//SYSUT2   DD SYSOUT=*',
            '//STEP040  EXEC PGM=GLSUBMIT,COND=(0,NE)',
            '//SUBFILE  DD DSN=PROD.GL.SUBMITTED,DISP=(NEW,CATLG)',
        ],
        [
            '//ARBILL   JOB (ACCT),\'AR BILLING RUN\',CLASS=A,MSGCLASS=X',
            '//STEP010  EXEC PGM=SORT',
            '//SORTIN   DD DSN=PROD.AR.ORDERS,DISP=SHR',
            '//SORTOUT  DD DSN=PROD.AR.SORTED,DISP=(NEW,CATLG)',
            '//STEP020  EXEC PGM=ARBILL01,COND=(4,LT)',
            '//INPUT    DD DSN=PROD.AR.SORTED,DISP=SHR',
            '//RATES    DD DSN=PROD.AR.PRICING,DISP=SHR',
            '//OUTPUT   DD DSN=PROD.AR.INVOICE(+1),DISP=(NEW,CATLG)',
            '//STEP030  EXEC PGM=IEBGENER,COND=(4,LT)',
            '//SYSUT1   DD DSN=PROD.AR.INVOICE(+1),DISP=SHR',
            '//SYSUT2   DD SYSOUT=*',
            '//STEP040  EXEC PGM=ARDISPAT,COND=(0,NE)',
            '//DISPFILE DD DSN=PROD.AR.DISPATCH,DISP=(NEW,CATLG)',
        ],
        [
            '//APDISB   JOB (ACCT),\'AP DISBURSEMENT\',CLASS=A,MSGCLASS=X',
            '//STEP010  EXEC PGM=SORT',
            '//SORTIN   DD DSN=PROD.AP.INVOICES,DISP=SHR',
            '//SORTOUT  DD DSN=PROD.AP.SORTED,DISP=(NEW,CATLG)',
            '//STEP020  EXEC PGM=APMATCH,COND=(4,LT)',
            '//INPUT    DD DSN=PROD.AP.SORTED,DISP=SHR',
            '//POFILE   DD DSN=PROD.AP.PURCHORD,DISP=SHR',
            '//OUTPUT   DD DSN=PROD.AP.APPROVED(+1),DISP=(NEW,CATLG)',
            '//STEP030  EXEC PGM=IEBGENER,COND=(4,LT)',
            '//SYSUT1   DD DSN=PROD.AP.APPROVED(+1),DISP=SHR',
            '//SYSUT2   DD SYSOUT=*',
            '//STEP040  EXEC PGM=APBACS,COND=(0,NE)',
            '//BACSFILE DD DSN=PROD.AP.BACS,DISP=(NEW,CATLG)',
        ],
        [
            '//INVPROC  JOB (ACCT),\'STOCK RECONCILE\',CLASS=A,MSGCLASS=X',
            '//STEP010  EXEC PGM=SORT',
            '//SORTIN   DD DSN=PROD.INV.MOVEMENTS,DISP=SHR',
            '//SORTOUT  DD DSN=PROD.INV.SORTED,DISP=(NEW,CATLG)',
            '//STEP020  EXEC PGM=INVRECON,COND=(4,LT)',
            '//INPUT    DD DSN=PROD.INV.SORTED,DISP=SHR',
            '//MASTER   DD DSN=PROD.INV.MASTER,DISP=OLD',
            '//OUTPUT   DD DSN=PROD.INV.MASTER,DISP=OLD',
            '//STEP030  EXEC PGM=IEBGENER,COND=(4,LT)',
            '//SYSUT1   DD DSN=PROD.INV.VARIANCE,DISP=SHR',
            '//SYSUT2   DD SYSOUT=*',
            '//STEP040  EXEC PGM=INVALERT,COND=(0,NE)',
            '//ALERTOUT DD DSN=PROD.INV.ALERTS,DISP=(NEW,CATLG)',
        ],
        [
            '//FXREVAL  JOB (ACCT),\'FX REVALUATION\',CLASS=A,MSGCLASS=X',
            '//STEP010  EXEC PGM=SORT',
            '//SORTIN   DD DSN=PROD.FX.POSITIONS,DISP=SHR',
            '//SORTOUT  DD DSN=PROD.FX.SORTED,DISP=(NEW,CATLG)',
            '//STEP020  EXEC PGM=FXRATE01,COND=(4,LT)',
            '//INPUT    DD DSN=PROD.FX.SORTED,DISP=SHR',
            '//RATES    DD DSN=PROD.FX.SPOTRATES,DISP=SHR',
            '//OUTPUT   DD DSN=PROD.FX.REVALUED(+1),DISP=(NEW,CATLG)',
            '//STEP030  EXEC PGM=IEBGENER,COND=(4,LT)',
            '//SYSUT1   DD DSN=PROD.FX.REVALUED(+1),DISP=SHR',
            '//SYSUT2   DD SYSOUT=*',
            '//STEP040  EXEC PGM=GLPOST01,COND=(0,NE)',
            '//GLFEED   DD DSN=PROD.GL.FXFEED,DISP=(NEW,CATLG)',
        ],
        [
            '//TAXCALC  JOB (ACCT),\'VAT CALCULATION\',CLASS=A,MSGCLASS=X',
            '//STEP010  EXEC PGM=SORT',
            '//SORTIN   DD DSN=PROD.TAX.TRANSACTIONS,DISP=SHR',
            '//SORTOUT  DD DSN=PROD.TAX.SORTED,DISP=(NEW,CATLG)',
            '//STEP020  EXEC PGM=VATCALC1,COND=(4,LT)',
            '//INPUT    DD DSN=PROD.TAX.SORTED,DISP=SHR',
            '//RATES    DD DSN=PROD.TAX.RATES,DISP=SHR',
            '//OUTPUT   DD DSN=PROD.TAX.RETURN(+1),DISP=(NEW,CATLG)',
            '//STEP030  EXEC PGM=IEBGENER,COND=(4,LT)',
            '//SYSUT1   DD DSN=PROD.TAX.RETURN(+1),DISP=SHR',
            '//SYSUT2   DD SYSOUT=*',
            '//STEP040  EXEC PGM=HMRCSUB,COND=(0,NE)',
            '//SUBFILE  DD DSN=PROD.TAX.HMRCSUB,DISP=(NEW,CATLG)',
        ],
        [
            '//PENSAUTO JOB (ACCT),\'PENSION ENROL\',CLASS=A,MSGCLASS=X',
            '//STEP010  EXEC PGM=SORT',
            '//SORTIN   DD DSN=PROD.PENS.PAYROLL,DISP=SHR',
            '//SORTOUT  DD DSN=PROD.PENS.SORTED,DISP=(NEW,CATLG)',
            '//STEP020  EXEC PGM=PENSELIG,COND=(4,LT)',
            '//INPUT    DD DSN=PROD.PENS.SORTED,DISP=SHR',
            '//SCHEME   DD DSN=PROD.PENS.SCHEME,DISP=SHR',
            '//OUTPUT   DD DSN=PROD.PENS.ENROLLED(+1),DISP=(NEW,CATLG)',
            '//STEP030  EXEC PGM=IEBGENER,COND=(4,LT)',
            '//SYSUT1   DD DSN=PROD.PENS.ENROLLED(+1),DISP=SHR',
            '//SYSUT2   DD SYSOUT=*',
            '//STEP040  EXEC PGM=PENSSUB,COND=(0,NE)',
            '//SUBFILE  DD DSN=PROD.PENS.PROVIDER,DISP=(NEW,CATLG)',
        ],
        [
            '//CYCCNT   JOB (ACCT),\'STOCK CYCLE COUNT\',CLASS=A,MSGCLASS=X',
            '//STEP010  EXEC PGM=SORT',
            '//SORTIN   DD DSN=PROD.CYC.COUNTS,DISP=SHR',
            '//SORTOUT  DD DSN=PROD.CYC.SORTED,DISP=(NEW,CATLG)',
            '//STEP020  EXEC PGM=CYCVAR01,COND=(4,LT)',
            '//INPUT    DD DSN=PROD.CYC.SORTED,DISP=SHR',
            '//MASTER   DD DSN=PROD.INV.MASTER,DISP=SHR',
            '//OUTPUT   DD DSN=PROD.CYC.VARIANCE(+1),DISP=(NEW,CATLG)',
            '//STEP030  EXEC PGM=IEBGENER,COND=(4,LT)',
            '//SYSUT1   DD DSN=PROD.CYC.VARIANCE(+1),DISP=SHR',
            '//SYSUT2   DD SYSOUT=*',
            '//STEP040  EXEC PGM=CYCALERT,COND=(0,NE)',
            '//ALERTOUT DD DSN=PROD.CYC.ALERTS,DISP=(NEW,CATLG)',
        ],
        [
            '//AUDITLOG JOB (ACCT),\'AUDIT LOG ARCHIVE\',CLASS=A,MSGCLASS=X',
            '//STEP010  EXEC PGM=SORT',
            '//SORTIN   DD DSN=PROD.AUD.RAWLOG,DISP=SHR',
            '//SORTOUT  DD DSN=PROD.AUD.SORTED,DISP=(NEW,CATLG)',
            '//STEP020  EXEC PGM=AUDCOMP1,COND=(4,LT)',
            '//INPUT    DD DSN=PROD.AUD.SORTED,DISP=SHR',
            '//RULES    DD DSN=PROD.AUD.RETRULES,DISP=SHR',
            '//OUTPUT   DD DSN=PROD.AUD.ARCHIVE(+1),DISP=(NEW,CATLG)',
            '//STEP030  EXEC PGM=IEBGENER,COND=(4,LT)',
            '//SYSUT1   DD DSN=PROD.AUD.ARCHIVE(+1),DISP=SHR',
            '//SYSUT2   DD SYSOUT=*',
            '//STEP040  EXEC PGM=TAPEARCH,COND=(0,NE)',
            '//TAPEOUT  DD DSN=PROD.AUD.TAPE,DISP=(NEW,CATLG)',
        ],
    ];

    type Line = { head: string; rest: string; isStep: boolean; isJobCard: boolean; cc: number; flushed: boolean };
    const lines: Line[] = [];
    const CYCLES = 4;
    for (let c = 0; c < CYCLES; c++) {
        JOBS.forEach((job) => {
            let prevFailed = false;
            job.forEach((raw, i) => {
                const [head, ...rest] = raw.split(/\s+/);
                const isStep = STEP_IDX.includes(i);
                let cc = 0;
                let flushed = false;
                if (isStep) {
                    const si = STEP_IDX.indexOf(i);
                    if (prevFailed && si > 0) {
                        flushed = true;
                    } else {
                        const roll = Math.random();
                        cc = roll < 0.78 ? 0 : roll < 0.93 ? 4 : 8;
                        if (cc >= 4) prevFailed = true;
                    }
                }
                lines.push({ head, rest: rest.join(' '), isStep, isJobCard: i === 0, cc, flushed });
            });
            lines.push({ head: '', rest: '', isStep: false, isJobCard: false, cc: 0, flushed: false });
        });
    }

    let base = 0;
    let next = 0;
    let pause = 0;

    return (frame) => {
        clear(frame, palette);
        const { ctx, width, height, time, delta, mouseY, hovering } = frame;
        const size = labelSize(height);
        const top = size * 1.4;
        const rowH = size * 1.55;
        const visibleCount = Math.max(1, Math.floor((height - top - size * 0.6) / rowH));

        if (pause > 0) pause -= delta;
        else if (time > next) {
            next = time + 0.48;
            base++;
            if (base >= lines.length) {
                base = 0;
                pause = 1.2;
            }
        }

        const hoveredRow = hovering && mouseY !== null ? Math.floor((mouseY - top) / rowH) : -1;

        ctx.font = `${size}px ${MONO}`;
        ctx.textBaseline = 'middle';

        for (let row = 0; row < visibleCount; row++) {
            const idx = base + row;
            if (idx < 0 || idx >= lines.length) continue;
            const line = lines[idx];
            const y = top + row * rowH + rowH / 2;

            if (row === hoveredRow) {
                ctx.fillStyle = alpha(pick(palette, 0), 0.12);
                ctx.fillRect(10, top + row * rowH, width - 20, rowH);
            }

            if (!line.head) continue;
            ctx.textAlign = 'left';
            ctx.fillStyle = alpha(line.isStep ? pick(palette, 0) : line.isJobCard ? pick(palette, 2) : pick(palette, 3), 0.9);
            ctx.fillText(line.head, 14, y);
            ctx.fillStyle = alpha(palette.foreground, line.flushed ? 0.3 : 0.75);
            const maxChars = Math.floor((width - size * 6.6 - 90) / (size * 0.62));
            ctx.fillText(line.rest.length > maxChars ? line.rest.slice(0, maxChars - 1) + '…' : line.rest, 14 + size * 6.6, y);
            if (line.isStep) {
                ctx.textAlign = 'right';
                const text = line.flushed ? 'FLUSH' : `CC ${String(line.cc).padStart(4, '0')}`;
                ctx.fillStyle = alpha(pick(palette, line.flushed ? 2 : line.cc >= 4 ? 1 : 0), 0.95);
                ctx.fillText(text, width - 14, y);
                ctx.textAlign = 'left';
            }
        }
    };
};

export const keyCrack = (palette: Palette, length = 18): Scene => {
    const glyphs = 'ABCDEF0123456789';
    const solved = Array.from({ length }, () => false);
    const value = Array.from({ length }, () => glyphs[Math.floor(Math.random() * glyphs.length)]);
    let next = 0;

    return (frame) => {
        clear(frame, palette);
        const { ctx, width, height, time } = frame;
        const size = Math.max(11, Math.min(20, width / (length * 0.9)));
        ctx.font = `${size}px ${MONO}`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';

        if (time > next) {
            next = time + 0.28;
            const remaining = solved.map((s, i) => (s ? -1 : i)).filter((i) => i >= 0);
            if (remaining.length) solved[remaining[0]] = true;
            else {
                solved.fill(false);
                for (let i = 0; i < length; i++) value[i] = glyphs[Math.floor(Math.random() * glyphs.length)];
            }
        }

        const cellW = (width - 24) / length;
        for (let i = 0; i < length; i++) {
            const x = 12 + i * cellW + cellW / 2;
            const y = height / 2;
            const character = solved[i] ? value[i] : glyphs[Math.floor(Math.random() * glyphs.length)];
            if (solved[i]) {
                ctx.fillStyle = alpha(pick(palette, i), 0.2);
                ctx.fillRect(x - cellW / 2 + 1, y - size, cellW - 2, size * 2);
            }
            ctx.fillStyle = solved[i] ? pick(palette, i) : alpha(palette.foreground, 0.35);
            ctx.fillText(character, x, y);
        }

        const done = solved.filter(Boolean).length / length;
        ctx.fillStyle = alpha(palette.foreground, 0.12);
        ctx.fillRect(12, height - 18, width - 24, 4);
        ctx.fillStyle = pick(palette, 0);
        ctx.fillRect(12, height - 18, (width - 24) * done, 4);
        ctx.textAlign = 'left';
    };
};

export const pinMux = (palette: Palette): Scene => {
    type Pin = { af: string; periph: number; shown: string; flip: number };
    const perSide = 6;
    const periphs = ['UART1', 'SPI1', 'I2C1', 'TIM2'];
    const afNumbers = [7, 5, 4, 1];
    const pins: Pin[] = Array.from({ length: perSide * 4 }, () => ({ af: 'GPIO', periph: -1, shown: 'GPIO', flip: 0 }));
    const assign = (side: number, slot: number, af: string, periph: number) => {
        pins[side * perSide + slot] = { af, periph, shown: 'GPIO', flip: 0 };
    };
    assign(0, 0, 'VDD', -2);
    assign(0, 5, 'GND', -2);
    assign(2, 0, 'VDD', -2);
    assign(2, 5, 'GND', -2);
    assign(0, 1, 'UART_TX', 0);
    assign(0, 2, 'UART_RX', 0);
    assign(1, 1, 'SPI_MOSI', 1);
    assign(1, 2, 'SPI_MISO', 1);
    assign(1, 3, 'SPI_SCK', 1);
    assign(1, 4, 'SPI_CS', 1);
    assign(2, 2, 'I2C_SCL', 2);
    assign(2, 3, 'I2C_SDA', 2);
    assign(3, 1, 'PWM_CH1', 3);
    assign(3, 2, 'PWM_CH2', 3);
    assign(3, 3, 'PWM_CH3', 3);
    assign(3, 4, 'PWM_CH4', 3);
    pins.forEach((pin) => {
        if (pin.periph === -2) pin.shown = pin.af;
    });
    let selected = 1;
    let next = 2.6;
    pins.forEach((pin) => {
        if (pin.periph === selected) pin.shown = pin.af;
    });

    return (frame) => {
        clear(frame, palette);
        const { ctx, width, height, delta, time, mouseX, mouseY, hovering } = frame;
        const pad = 14;
        const size = labelSize(height);
        const pinLen = size * 0.9;
        const labelRoom = size * 5.4;
        const side = Math.min(width * 0.4, height - pad * 2 - 2 * (pinLen + labelRoom));
        const cx = width / 2;
        const cy = height / 2 + size * 0.4;
        const chipL = cx - side / 2;
        const chipT = cy - side / 2;

        if (time > next) {
            next = time + 2.2;
            selected = (selected + 1) % (periphs.length + 1);
            pins.forEach((pin) => {
                if (pin.periph < 0) return;
                const target = pin.periph === selected ? pin.af : 'GPIO';
                if (target !== pin.shown) pin.flip = 1;
            });
        }
        pins.forEach((pin) => {
            if (pin.flip <= 0) return;
            pin.flip = Math.max(0, pin.flip - delta * 2.4);
            if (pin.flip < 0.5 && pin.periph >= 0) pin.shown = pin.periph === selected ? pin.af : 'GPIO';
        });

        ctx.fillStyle = alpha(palette.foreground, 0.04);
        ctx.fillRect(chipL, chipT, side, side);
        ctx.strokeStyle = alpha(palette.foreground, 0.35);
        ctx.lineWidth = 1.2;
        ctx.strokeRect(chipL + 0.5, chipT + 0.5, side - 1, side - 1);
        ctx.fillStyle = alpha(palette.foreground, 0.25);
        ctx.beginPath();
        ctx.arc(chipL + side * 0.1, chipT + side * 0.1, 2.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.font = `${size}px ${MONO}`;
        ctx.textBaseline = 'middle';
        const pitch = side / perSide;

        const pinSide = (i: number) => Math.floor(i / perSide);
        const pinAlong = (i: number) => ((i % perSide) + 0.5) * pitch;
        let hoveredIndex = -1;
        if (hovering && mouseX !== null && mouseY !== null) {
            for (let i = 0; i < pins.length; i++) {
                const s = pinSide(i);
                const along = pinAlong(i);
                const reach = pinLen + labelRoom;
                const hit =
                    s === 0
                        ? mouseX >= chipL - reach && mouseX <= chipL && Math.abs(mouseY - (chipT + along)) <= pitch / 2
                        : s === 1
                        ? mouseY >= chipT - reach && mouseY <= chipT && Math.abs(mouseX - (chipL + along)) <= pitch / 2
                        : s === 2
                        ? mouseX >= chipL + side && mouseX <= chipL + side + reach && Math.abs(mouseY - (chipT + along)) <= pitch / 2
                        : mouseY >= chipT + side && mouseY <= chipT + side + reach && Math.abs(mouseX - (chipL + along)) <= pitch / 2;
                if (hit) {
                    hoveredIndex = i;
                    break;
                }
            }
        }

        pins.forEach((pin, i) => {
            const s = pinSide(i);
            const along = pinAlong(i);
            const hovered = i === hoveredIndex;
            const isActive = pin.periph >= 0 && pin.periph === selected;
            const ownColour = pin.periph >= 0 ? pick(palette, pin.periph) : pin.periph === -2 ? palette.muted : palette.foreground;
            const tint = hovered ? ownColour : isActive ? pick(palette, selected) : pin.periph === -2 ? palette.muted : palette.foreground;
            const pulse = isActive ? 0.8 + 0.2 * Math.sin(time * 3 + i) : 1;
            const fade = pin.flip > 0 ? Math.abs(1 - pin.flip * 2) : 1;
            ctx.fillStyle = alpha(tint, hovered ? 1 : (isActive ? 0.9 * pulse : pin.periph === -2 ? 0.35 : 0.3));
            let lx = 0;
            let ly = 0;
            if (s === 0) {
                ctx.fillRect(chipL - pinLen, chipT + along - 2.5, pinLen, 5);
                lx = chipL - pinLen - 5;
                ly = chipT + along;
            } else if (s === 1) {
                ctx.fillRect(chipL + along - 2.5, chipT - pinLen, 5, pinLen);
                lx = chipL + along;
                ly = chipT - pinLen - 5;
            } else if (s === 2) {
                ctx.fillRect(chipL + side, chipT + along - 2.5, pinLen, 5);
                lx = chipL + side + pinLen + 5;
                ly = chipT + along;
            } else {
                ctx.fillRect(chipL + along - 2.5, chipT + side, 5, pinLen);
                lx = chipL + along;
                ly = chipT + side + pinLen + 5;
            }
            if (isActive) {
                const t = (time * 0.6 + i * 0.17) % 1;
                const dotPos = s === 0 ? [chipL - pinLen * t, chipT + along] : s === 1 ? [chipL + along, chipT - pinLen * t] : s === 2 ? [chipL + side + pinLen * t, chipT + along] : [chipL + along, chipT + side + pinLen * t];
                ctx.fillStyle = alpha(pick(palette, selected), 0.9 * pulse);
                ctx.beginPath();
                ctx.arc(dotPos[0], dotPos[1], 1.6, 0, Math.PI * 2);
                ctx.fill();
            }
            const label = hovered ? pin.af : pin.shown;
            ctx.fillStyle = alpha(tint, hovered ? 1 : (isActive ? 1 : pin.periph === -2 ? 0.4 : 0.4) * fade);
            if (s === 0 || s === 2) {
                ctx.textAlign = s === 0 ? 'right' : 'left';
                ctx.fillText(label, lx, ly);
            } else {
                ctx.save();
                ctx.translate(lx, ly);
                ctx.rotate(-Math.PI / 2);
                ctx.textAlign = s === 1 ? 'left' : 'right';
                ctx.fillText(label, 0, 0);
                ctx.restore();
            }
        });

        ctx.textAlign = 'center';
        ctx.fillStyle = alpha(palette.foreground, 0.4);
        ctx.fillText('MCU', cx, cy - size * 0.9);
        const active = selected < periphs.length;
        ctx.fillStyle = active ? pick(palette, selected) : alpha(palette.foreground, 0.3);
        ctx.fillText(active ? periphs[selected] : 'all GPIO', cx, cy + size * 0.9);

        ctx.textAlign = 'right';
        ctx.textBaseline = 'top';
        ctx.fillStyle = active ? pick(palette, selected) : alpha(palette.foreground, 0.35);
        ctx.fillText(active ? `${periphs[selected]} → AF${afNumbers[selected]}` : 'AF0 reset', width - pad, pad - 2);
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';
    };
};

export const beeswarm = (palette: Palette): Scene => {
    type Pt = { cat: number; v: number; x: number; y: number; tx: number; scale: number; dying: boolean };
    type HoverPt = { cat: number; v: number; x: number; y: number; tx: number; ty: number; scale: number; bornAt: number };
    const cats = ['control', 'variant a', 'variant b', 'variant c', 'holdout'];
    const capacity = 110;
    const pts: Pt[] = [];
    const hoverPts: HoverPt[] = [];
    /* Live points join/leave instantly (birth/death is a scale fade, not a membership fade), which
       would otherwise snap the mean every time a point spawns or starts dying. Easing the mean value
       itself — not just its pixel position — keeps the line and its label in sync. */
    const meanState: { value: number; seeded: boolean }[] = cats.map(() => ({ value: 0, seeded: false }));
    let next = 0;
    let hoverNext = 0;
    let seeded = false;

    const place = (cat: number, v: number, cx: number, toY: (v: number) => number, radius: number) => {
        const y = toY(v);
        const near = pts.filter((p) => p.cat === cat && !p.dying && Math.abs(p.y - y) < radius * 2.1);
        for (let k = 0; k < 60; k++) {
            const candidate = cx + (k % 2 === 0 ? 1 : -1) * Math.ceil(k / 2) * radius * 2.15;
            if (!near.some((p) => Math.abs(p.tx - candidate) < radius * 2.05)) return candidate;
        }
        return cx;
    };

    return (frame) => {
        clear(frame, palette);
        const { ctx, width, height, delta, time, mouseX, mouseY, hovering } = frame;
        const size = label(ctx, height);
        const top = size * 0.8;
        const bottom = height - size * 1;
        const colW = (width - 28) / cats.length;
        const radius = Math.max(2.4, Math.min(3.6, height / 75));
        const toY = (v: number) => bottom - v * (bottom - top);
        const draw = (cat: number) => Math.max(0.02, Math.min(0.98, 0.32 + cat * 0.08 + gaussian() * 0.12));
        if (!seeded) {
            seeded = true;
            for (let i = 0; i < capacity; i++) {
                const cat = i % cats.length;
                const v = draw(cat);
                const cx = 14 + colW * (cat + 0.5);
                const tx = place(cat, v, cx, toY, radius);
                pts.push({ cat, v, x: tx, y: toY(v), tx, scale: 1, dying: false });
            }
        }
        if (time > next) {
            next = time + 0.11;
            const cat = Math.floor(random(0, cats.length));
            const v = draw(cat);
            const cx = 14 + colW * (cat + 0.5);
            const oldest = pts.find((p) => !p.dying);
            if (oldest) oldest.dying = true;
            const tx = place(cat, v, cx, toY, radius);
            pts.push({ cat, v, x: cx, y: top - 12, tx, scale: 0, dying: false });
        }
        if (hovering && mouseX !== null && mouseY !== null && time > hoverNext) {
            hoverNext = time + 1 / 15;
            const cat = Math.max(0, Math.min(cats.length - 1, Math.floor((mouseX - 14) / colW)));
            const v = Math.max(0.02, Math.min(0.98, (bottom - mouseY) / (bottom - top)));
            const scatter = radius * 5;
            hoverPts.push({
                cat, v, x: mouseX, y: mouseY,
                tx: mouseX + random(-scatter, scatter), ty: mouseY + random(-scatter, scatter),
                scale: 0, bornAt: time,
            });
        }
        ctx.strokeStyle = alpha(palette.foreground, 0.08);
        ctx.lineWidth = 1;
        for (let i = 1; i < 4; i++) {
            const y = top + ((bottom - top) / 4) * i;
            ctx.beginPath();
            ctx.moveTo(14, y);
            ctx.lineTo(width - 14, y);
            ctx.stroke();
        }
        ctx.strokeStyle = alpha(palette.foreground, 0.14);
        cats.forEach((_, i) => {
            const cx = 14 + colW * (i + 0.5);
            ctx.beginPath();
            ctx.moveTo(cx, top);
            ctx.lineTo(cx, bottom);
            ctx.stroke();
        });
        for (let i = pts.length - 1; i >= 0; i--) {
            const p = pts[i];
            p.scale = ease(p.scale, p.dying ? 0 : 1, delta, 7);
            if (p.dying && p.scale < 0.03) {
                pts.splice(i, 1);
                continue;
            }
            p.x = ease(p.x, p.tx, delta, 8);
            p.y = ease(p.y, toY(p.v), delta, 8);
            ctx.fillStyle = alpha(pick(palette, p.cat), 0.82 * Math.min(1, p.scale));
            ctx.beginPath();
            ctx.arc(p.x, p.y, radius * p.scale, 0, Math.PI * 2);
            ctx.fill();
        }
        for (let i = hoverPts.length - 1; i >= 0; i--) {
            const p = hoverPts[i];
            const age = time - p.bornAt;
            if (age >= 3) {
                hoverPts.splice(i, 1);
                continue;
            }
            const targetScale = age > 2.7 ? Math.max(0, (3 - age) / 0.3) : 1;
            p.scale = ease(p.scale, targetScale, delta, 10);
            p.x = ease(p.x, p.tx, delta, 6);
            p.y = ease(p.y, p.ty, delta, 6);
            ctx.fillStyle = alpha(pick(palette, p.cat), 0.85 * p.scale);
            ctx.beginPath();
            ctx.arc(p.x, p.y, radius * 0.8 * p.scale, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.textAlign = 'center';
        cats.forEach((_, i) => {
            const live = [...pts.filter((p) => p.cat === i && !p.dying), ...hoverPts.filter((p) => p.cat === i)];
            if (!live.length) return;
            const rawMean = live.reduce((s, p) => s + p.v, 0) / live.length;
            const state = meanState[i];
            if (!state.seeded) {
                state.value = rawMean;
                state.seeded = true;
            } else {
                state.value = ease(state.value, rawMean, delta, 4);
            }
            const cx = 14 + colW * (i + 0.5);
            const y = toY(state.value);
            ctx.strokeStyle = alpha(palette.foreground, 0.95);
            ctx.lineWidth = 1.8;
            ctx.beginPath();
            ctx.moveTo(cx - colW * 0.42, y);
            ctx.lineTo(cx + colW * 0.42, y);
            ctx.stroke();
            ctx.fillStyle = alpha(palette.foreground, 0.55);
            ctx.fillText(`x̄ ${(state.value * 100).toFixed(1)}`, cx, y - size * 0.9);
        });
        ctx.textAlign = 'left';
    };
};
